import { isETHAddressValid } from "./../../utils/strings";
import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import {
  Balances,
  ProcessingState,
  RoutePath,
  SwapMode,
} from "@src/interfaces";
import { getRoute } from "@src/services";
import {
  Chain,
  Route,
  TokenPrices,
  SwapperAppPayload,
  ContractCall,
} from "@src/models";
import { ethers } from "ethers";
import { TokenWithChain } from "@src/models";
import {
  calculateFees,
  humanReadableToWei,
  logger,
  weiToHumanReadable,
} from "@src/utils";
import {
  CASH_TOKEN_ADDRESS_BY_CHAIN,
  DEFAULT_REQUEST_ABORT_MSG,
  shift4Config,
} from "@src/config";
import { Shift4Fees } from "./types";
import { QuoteRequestPayload, QuoteResponse } from "./useShift4Api";
import { useLocation } from "react-router-dom";
import { contractAddresses } from "@src/config";

export const useRouteState = (
  chain: Chain | undefined,
  token: TokenWithChain | undefined,
  cashToken: TokenWithChain | undefined,
  cashAmount: string,
  tokenAmount: string,
  swapMode: SwapMode,
  tokensBalances: Record<string, Record<string, Balances>>,
  integrationConfig: Partial<SwapperAppPayload>,
  smartWalletAddress: string | undefined,
  signerWalletAddress: string | undefined,
  tokensPrices: TokenPrices,
  transactionState: ProcessingState,
  isUsingChainlinkFunction: boolean,
  getShift4Quote: (
    payload: QuoteRequestPayload,
    chain: Chain,
  ) => Promise<QuoteResponse>,
) => {
  const location = useLocation();
  const [route, setRoute] = useState<Route | undefined>(undefined);
  const [customContractCalls, setCustomContractCalls] = useState<
    ContractCall[] | undefined
  >(undefined);
  const [isFetchingRoute, setIsFetchingRoute] = useState(false);
  const [shift4TokenAmount, setShift4TokenAmount] = useState("");
  const [shift4Fees, setShift4Fees] = useState<Shift4Fees | undefined>(
    undefined,
  );
  const [slippage, setSlippage] = useState(1.5);
  const [routeError, setRouteError] = useState("");
  const [routeErrorDetails, setRouteErrorDetails] = useState("");
  const [feesToTokenValueRatio, setFeesToTokenValueRatio] = useState(0);
  const [acceptedHighFees, setAcceptedHighFees] = useState(false);

  const routeAbortController = useRef<AbortController | null>(null);

  const tokenAmountWei = useMemo(() => {
    if (!token) {
      return "0";
    }
    return humanReadableToWei({
      amount: tokenAmount,
      decimals: token.decimals,
    });
  }, [token, tokenAmount]);

  const estimatedTokenOutAmountWei = useMemo(() => {
    return route?.estAmountOut || "0";
  }, [route]);

  const estimatedTokenOutAmount = useMemo(() => {
    if (!route?.estAmountOut || !token) {
      return "0";
    }

    // Determine which token's decimals to use based on the swap mode
    const outputToken = swapMode === SwapMode.Buy ? token : cashToken;
    if (!outputToken) {
      return "0";
    }

    // Convert wei to human readable format
    return weiToHumanReadable({
      amount: route.estAmountOut,
      decimals: outputToken.decimals,
      precisionFractionalPlaces: 5,
      prettifySmallNumber: true,
    });
  }, [route, token, cashToken, swapMode]);

  const abortPrevRequest = useCallback(() => {
    if (routeAbortController.current) {
      routeAbortController.current.abort(DEFAULT_REQUEST_ABORT_MSG);
      routeAbortController.current = null;
    }
    setIsFetchingRoute(false);
  }, []);

  const resetRouteState = useCallback((stealthMode) => {
    if (!stealthMode) {
      setRouteError("");
      setRouteErrorDetails("");
      setFeesToTokenValueRatio(0);
      setShift4Fees(undefined);
      setIsFetchingRoute(true);
    }
  }, []);

  const fetchRouteValidation = useCallback(() => {
    if (
      !(
        location.pathname === RoutePath.Buy ||
        location.pathname === RoutePath.Sell ||
        (location.pathname === RoutePath.Processing &&
          transactionState === ProcessingState.DepositSuccess)
      )
    ) {
      return false;
    }
    if (
      !cashToken ||
      !chain ||
      !token ||
      !(swapMode === SwapMode.Buy ? cashAmount : tokenAmount)
    ) {
      abortPrevRequest();
      setRoute(undefined);
      return false;
    }
    return true;
  }, [
    location.pathname,
    abortPrevRequest,
    cashToken,
    chain,
    token,
    swapMode,
    cashAmount,
    tokenAmount,
    transactionState,
  ]);

  const createShift4QuotePayload = useCallback(
    (chain: Chain, fiatAmount?: string): QuoteRequestPayload => {
      const basePayload: QuoteRequestPayload = {
        fiatAmount: fiatAmount || cashAmount,
        cryptoCurrencyCode: fiatAmount
          ? shift4Config[chain.chainId]!.USDC
          : isUsingChainlinkFunction
          ? shift4Config[chain.chainId]!.NATIVE
          : shift4Config[chain.chainId]!.USDC,
        fiatCurrencyCode: "USD",
        walletAddress:
          smartWalletAddress ||
          contractAddresses[chain.chainId]?.FeeCollector ||
          "",
      };

      if (!fiatAmount && isUsingChainlinkFunction) {
        basePayload.walletAddress =
          contractAddresses[chain.chainId]?.SwapperRouter || "";
        basePayload.contractExecution = {
          contractAddress: contractAddresses[chain.chainId]?.SwapperRouter,
          contractId: "a6f332d6-7b3e-48fb-8051-40bfdd827c7e", // mocked id
          payload: {
            tokenIn: ethers.constants.AddressZero,
            tokenOut: token?.address,
            receiver:
              smartWalletAddress ||
              contractAddresses[chain.chainId]?.SwapperRouter,
            metadata: {
              routeHash:
                "0x4cbfabaf87c27fcfd9af7a49a089af50065bc553424b1e279dff03c9692a49a3", // mocked hash
              gasLimit: "1000000",
            },
            swapperCalls: [
              {
                callType: 1,
                target:
                  process.env.NODE_ENV === "development"
                    ? CASH_TOKEN_ADDRESS_BY_CHAIN[chain.chainId] // on testnet this address is whitelisted
                    : "0x6d7bd5c0c207cba1377f58048aa5f8c5cd933613", // on mainnet this address is whitelisted (XSwap Executor)
                value: "0",
                payload:
                  process.env.NODE_ENV === "development"
                    ? "0x0000000000000000000000001c7d4b196cb0c7b01d743fbc6116a902379c72380000000000000000000000000000000000000000000000000000000000000001"
                    : "0x000000000000000000000000a0b86991c6218b36c1d19d4a2e9eb0ce3606eb480000000000000000000000000000000000000000000000000000000000000001",
                callData:
                  process.env.NODE_ENV === "development"
                    ? "0x095ea7b3000000000000000000000000124ec2fde4ee78255ed6371b3b401c854de77de20000000000000000000000000000000000000000000000000000000000000000"
                    : "0x095ea7b3000000000000000000000000757965b16169e21535a0f405140097d83bf10ec80000000000000000000000000000000000000000000000000000000000000000",
              },
            ],
          },
        };
      }

      return basePayload;
    },
    [cashAmount, isUsingChainlinkFunction, smartWalletAddress, token?.address],
  );

  const handleShift4QuoteResponse = useCallback((response: QuoteResponse) => {
    let shift4Amount = "";
    if (response.data.quote.cryptoAmount) {
      shift4Amount = response.data.quote.cryptoAmount.toString();
      setShift4TokenAmount(shift4Amount);
    }
    let shift4Fees: Shift4Fees | undefined;
    if (response.data.quote) {
      const {
        processingFeeFiatAmount,
        partnerFeeFiatAmount,
        networkFeeFiatAmount,
      } = response.data.quote;
      shift4Fees = {
        processingFeeFiatAmount,
        partnerFeeFiatAmount,
        networkFeeFiatAmount,
      };
      setShift4Fees(shift4Fees);
    }

    return { shift4Amount, shift4Fees };
  }, []);

  const handleShift4Error = useCallback((err: Error | unknown) => {
    console.error(err);
    setShift4Fees(undefined);
    setShift4TokenAmount("");
    setRoute(undefined);

    try {
      const errMsg = err instanceof Error ? err.message : String(err);
      const parsedError = JSON.parse(errMsg);
      if (parsedError.data.errorType === "err.invalidAmount") {
        if (parsedError.data.meta.maxAmount) {
          setRouteErrorDetails(
            `${parsedError.data.errorMessage} Max: ${parsedError.data.meta.maxAmount}`,
          );
        } else if (parsedError.data.meta.minAmount) {
          setRouteErrorDetails(
            `${parsedError.data.errorMessage} Min: ${parsedError.data.meta.minAmount}`,
          );
        } else {
          setRouteErrorDetails(`${parsedError.data.errorMessage}`);
        }
      }
    } catch (e) {
      // ignore parsing error
    }

    setRouteError("Price unavailable");
    setIsFetchingRoute(false);
    return undefined;
  }, []);

  const calculateFromAmount = useCallback(
    (shift4Amount: string | undefined) => {
      return humanReadableToWei({
        amount:
          swapMode === SwapMode.Buy ? shift4Amount || cashAmount : tokenAmount,
        decimals:
          swapMode === SwapMode.Buy
            ? shift4Amount && isUsingChainlinkFunction
              ? 18 // ETH on ethereum chain
              : 6 // USDC on ethereum chain
            : token?.decimals || 0,
      });
    },
    [
      swapMode,
      cashAmount,
      tokenAmount,
      isUsingChainlinkFunction,
      token?.decimals,
    ],
  );

  const createRouteParams = useCallback(
    (fromAmount: string, chain: Chain) => {
      const ext = integrationConfig.externalWalletAddress;
      const hasValidExternal =
        typeof ext === "string" && isETHAddressValid(ext);

      const finalReceiverWalletAddress =
        swapMode === SwapMode.Sell
          ? smartWalletAddress
          : hasValidExternal
          ? ext
          : smartWalletAddress;

      const toAddress =
        finalReceiverWalletAddress ||
        contractAddresses[chain.chainId]?.FeeCollector;

      return {
        integratorId: integrationConfig.integratorId!,
        chainId: chain.chainId,
        fromAddress:
          smartWalletAddress || contractAddresses[chain.chainId]?.FeeCollector,
        signerAddress: signerWalletAddress,
        toAddress,
        fromToken:
          swapMode === SwapMode.Buy
            ? isUsingChainlinkFunction
              ? ethers.constants.AddressZero
              : CASH_TOKEN_ADDRESS_BY_CHAIN[chain.chainId]
            : token!.address,
        toToken:
          swapMode === SwapMode.Sell
            ? CASH_TOKEN_ADDRESS_BY_CHAIN[chain.chainId]
            : token!.address,
        fromAmount: fromAmount,
        slippage: slippage,
        customContractCalls:
          customContractCalls || integrationConfig.customContractCalls,
        isUsingChainlinkFunction: isUsingChainlinkFunction,
      };
    },
    [
      integrationConfig.externalWalletAddress,
      integrationConfig.integratorId,
      integrationConfig.customContractCalls,
      smartWalletAddress,
      signerWalletAddress,
      swapMode,
      isUsingChainlinkFunction,
      token,
      slippage,
      customContractCalls,
    ],
  );

  const checkAndSetFeeWarning = useCallback(
    ({ route, cashAmount, tokenAmount, token, cashToken, shift4Fees }) => {
      const { totalFees } = calculateFees({
        route,
        cashToken,
        shift4Fees,
      });

      let calculatedPercentage = 0;

      if (swapMode === SwapMode.Buy && cashAmount) {
        calculatedPercentage = Number(totalFees) / Number(cashAmount);
      } else if (swapMode === SwapMode.Sell && tokenAmount && token) {
        const tokenPrice = Number(
          tokensPrices[token.chainId]?.[token?.address],
        );
        if (!tokenPrice) return;

        calculatedPercentage =
          Number(totalFees) / (Number(tokenAmount) * tokenPrice);
      }

      setFeesToTokenValueRatio(calculatedPercentage);
    },
    [swapMode, tokensPrices],
  );

  const handleMinTransactionValueError = useCallback(
    (error: Error | unknown, shift4Amount: string | undefined) => {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      if (errorMessage.indexOf("Minimum transaction value not met") > -1) {
        try {
          const parsedError = JSON.parse(errorMessage);

          if (swapMode === SwapMode.Buy) {
            // We need to adjust minimal amount to type in case we are using shift4 deposit because they are charging some fees and the actual amount that we are swapping will be little less then user typed
            const multiplier = shift4Amount
              ? Number(cashAmount) / Number(shift4Amount)
              : 1;

            setRouteErrorDetails(
              `You need to type at least $${weiToHumanReadable({
                amount: (
                  multiplier * Number(parsedError.message.minTxValue)
                ).toFixed(0),
                decimals: 6,
                precisionFractionalPlaces: 2,
              })} due to current network congestion`,
            );
          }

          if (swapMode === SwapMode.Sell && token) {
            const minTxValue = weiToHumanReadable({
              amount: parsedError.message.minTxValue,
              decimals: 6,
              precisionFractionalPlaces: 2,
            });
            const price = tokensPrices[token.chainId]?.[token.address];
            const minTokenAmount = Number(minTxValue) / Number(price);

            setRouteErrorDetails(
              `You need to type at least ${minTokenAmount.toFixed(5)} ${
                token.symbol
              } due to current network congestion`,
            );
          }
        } catch (e) {
          // ignore parsing error
        }
      }
    },
    [swapMode, cashAmount, token, tokensPrices],
  );

  const handleRouteError = useCallback(
    (error: Error | unknown, shift4Amount: string | undefined) => {
      if (error !== DEFAULT_REQUEST_ABORT_MSG) {
        console.error(error);
        setRouteError("Price unavailable");
        setRoute(undefined);
        setIsFetchingRoute(false);

        handleMinTransactionValueError(error, shift4Amount);
      }
    },
    [handleMinTransactionValueError],
  );

  const latestRouteFetchId = useRef(0);
  const latestShift4QuoteFetchId = useRef(0);

  const fetchShift4Quote = useCallback(
    async (fiatAmount: string) => {
      if (!chain) return;
      resetRouteState(false);
      const currentFetchId = ++latestShift4QuoteFetchId.current;
      try {
        const quotePayload = createShift4QuotePayload(chain, fiatAmount);
        const response = await getShift4Quote(quotePayload, chain);
        if (latestShift4QuoteFetchId.current !== currentFetchId) return;
        setIsFetchingRoute(false);
        handleShift4QuoteResponse(response);
      } catch (err: unknown) {
        if (latestShift4QuoteFetchId.current !== currentFetchId) return;
        handleShift4Error(err);
        setIsFetchingRoute(false);
        return;
      }
    },
    [
      resetRouteState,
      chain,
      createShift4QuotePayload,
      getShift4Quote,
      handleShift4QuoteResponse,
      handleShift4Error,
    ],
  );

  const fetchRoute = useCallback(
    async (stealthMode = false) => {
      if (!fetchRouteValidation()) {
        return;
      }
      const currentFetchId = ++latestRouteFetchId.current;

      // Check if a Shift4 quote should be fetched
      const cashBalance = Number(
        tokensBalances[cashToken!.chainId]?.[cashToken!.address]
          ?.humanReadable || "0",
      );
      const shouldFetchQuote = !!(
        location.pathname === RoutePath.Buy &&
        Number(cashAmount) > Number(cashBalance) &&
        chain
      );

      resetRouteState(stealthMode);

      // Handle Shift4 quote if needed
      let shift4Amount: string | undefined;
      let shift4Fees: Shift4Fees | undefined;
      if (shouldFetchQuote) {
        try {
          const quotePayload = createShift4QuotePayload(chain);
          const response = await getShift4Quote(quotePayload, chain);
          if (latestRouteFetchId.current !== currentFetchId) return;
          ({ shift4Amount, shift4Fees } = handleShift4QuoteResponse(response));
        } catch (err: unknown) {
          if (latestRouteFetchId.current !== currentFetchId) return;
          handleShift4Error(err);
          return;
        }
      }

      // Calculate the amount for the route
      const fromAmount = calculateFromAmount(shift4Amount || shift4TokenAmount);

      // Get the route
      try {
        // Abort any existing request
        if (routeAbortController.current) {
          routeAbortController.current.abort(DEFAULT_REQUEST_ABORT_MSG);
        }

        // Create a new controller for this request
        routeAbortController.current = new AbortController();

        // Get the route with parameters
        const routeParams = createRouteParams(fromAmount, chain!);
        const route = await getRoute(
          routeParams,
          routeAbortController.current.signal,
        );
        if (latestRouteFetchId.current !== currentFetchId) return;
        // Update state with the result
        setRoute(route);
        checkAndSetFeeWarning({
          route,
          cashAmount,
          tokenAmount,
          token,
          cashToken,
          shift4Fees,
        });
        setIsFetchingRoute(false);
        routeAbortController.current = null;
        if (stealthMode) {
          setIsFetchingRoute(true);
          setTimeout(() => {
            if (latestRouteFetchId.current !== currentFetchId) return;
            setRouteError("");
            setRouteErrorDetails("");
            setIsFetchingRoute(false);
          }, 400);
        }
      } catch (error: Error | unknown) {
        if (latestRouteFetchId.current !== currentFetchId) return;
        handleRouteError(error, shift4Amount);
      }
    },
    [
      chain,
      cashToken,
      cashAmount,
      tokensBalances,
      getShift4Quote,
      fetchRouteValidation,
      resetRouteState,
      createShift4QuotePayload,
      handleShift4QuoteResponse,
      handleShift4Error,
      calculateFromAmount,
      createRouteParams,
      handleRouteError,
      location.pathname,
      checkAndSetFeeWarning,
      token,
      tokenAmount,
      shift4TokenAmount,
    ],
  );

  useEffect(() => {
    logger.log({ route });
  }, [route]);

  return {
    route,
    setRoute,
    isFetchingRoute,
    fetchShift4Quote,
    fetchRoute,
    shift4TokenAmount,
    setShift4TokenAmount,
    shift4Fees,
    setShift4Fees,
    error: routeError,
    setError: setRouteError,
    errorDetails: routeErrorDetails,
    setErrorDetails: setRouteErrorDetails,
    feesToTokenValueRatio,
    setFeesToTokenValueRatio,
    acceptedHighFees,
    setAcceptedHighFees,
    tokenAmountWei,
    estimatedTokenOutAmount,
    estimatedTokenOutAmountWei,
    slippage,
    setSlippage,
    customContractCalls,
    setCustomContractCalls,
  };
};
